import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, App, Button, Card, Col, Form, Image, Input, Radio, Row, Select, Space, Spin, Tag, Typography } from 'antd';
import { CheckOutlined, DeleteOutlined, MinusCircleOutlined, PlusOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { EMAIL_LABELS, PHONE_LABELS, emptyCardDetails, type CardAudience, type CardDetails, type CardScanMatch } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { RecordError } from '../../components/states';
import { errorMessage, fileUrl } from '../../api/client';
import { palette } from '../../app/theme';
import { CardTitle } from '../../components/StatCard';
import { useAddedByTag, useCardScan, useCardScanMatches, useCardScans, useDiscardCardScan, useEventTags, useRetryCardScan, useSaveCardScan, useUpdateCardScan } from '../../features/cardScans/api';
import { EventPicker, NO_EVENT } from './EventPicker';
import { CardAudienceFields, useKnownAudience } from './CardAudienceFields';
import { SCAN_PATH } from './ScanCardsPage';

const NEW = 'new';
const LINKS: { key: 'website' | 'linkedin' | 'facebook' | 'instagram' | 'x' | 'tiktok'; label: string }[] = [
  { key: 'website', label: 'Website' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'x', label: 'X (Twitter)' },
  { key: 'tiktok', label: 'TikTok' },
];
const PHONE_LABEL_OPTIONS = [...PHONE_LABELS, 'Fax'].map((l) => ({ value: l, label: l }));
const EMAIL_LABEL_OPTIONS = EMAIL_LABELS.map((l) => ({ value: l, label: l }));
const REASON: Record<CardScanMatch['reasons'][number], string> = { email: 'same email', phone: 'same phone', name: 'same name' };

/** Waits until the details stop changing, so matches are looked up once typing pauses. */
function useSettled<T>(value: T, ms = 600) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/** Check one scanned card: the photos above, what was read below (unsure fields marked), then save or discard. */
export function ScanReviewPage() {
  const { id } = useParams<{ id: string }>();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const scan = useCardScan(id);
  const queue = useCardScans();
  const addedBy = useAddedByTag();
  const eventTags = useEventTags();
  const save = useSaveCardScan();
  const discard = useDiscardCardScan();
  const retry = useRetryCardScan();
  const update = useUpdateCardScan();
  const [details, setDetails] = useState<CardDetails | null>(null);
  const [eventTagId, setEventTagId] = useState(NO_EVENT);
  const [picked, setPicked] = useState<CardAudience>({ tagIds: [], listIds: [], segmentIds: [] });
  const audience = useKnownAudience(picked);
  const [target, setTarget] = useState<string | null>(null);
  const [allLinks, setAllLinks] = useState(false);

  // a fresh form per card, filled from what was read
  const s = scan.data;
  useEffect(() => {
    setDetails(null);
    setTarget(null);
    setAllLinks(false);
  }, [id]);
  useEffect(() => {
    if (s && s.id === id && !details && s.status === 'READY') {
      setDetails(s.extracted ?? emptyCardDetails());
      setEventTagId(s.eventTag?.id ?? NO_EVENT);
      setPicked({ tagIds: s.tagIds, listIds: s.listIds, segmentIds: s.segmentIds });
    }
  }, [s, id, details]);

  const settled = useSettled(details);
  const matches = useCardScanMatches(settled, !!settled);
  // the best match is picked once, when it is clearly the same person; the choice is then the user's
  useEffect(() => {
    if (target !== null || !matches.data) return;
    const strong = matches.data.find((m) => m.reasons.includes('email') || m.reasons.includes('phone'));
    setTarget(strong?.id ?? NEW);
  }, [matches.data, target]);

  const ready = (queue.data ?? []).filter((q) => q.status === 'READY');
  const position = ready.findIndex((q) => q.id === id);
  const next = ready.find((q) => q.id !== id);
  const goNext = () => navigate(next ? `${SCAN_PATH}/${next.id}` : SCAN_PATH);
  const eventName = useMemo(() => (eventTagId ? (eventTags.data?.find((t) => t.id === eventTagId)?.name ?? s?.eventTag?.name) : null), [eventTagId, eventTags.data, s?.eventTag]);

  if (scan.isError) return <RecordError error={scan.error} what="card" backTo={SCAN_PATH} backLabel="Back to Scan cards" onRetry={() => scan.refetch()} />;
  if (!s) return <Spin style={{ display: 'block', margin: 48 }} />;

  const set = (patch: Partial<CardDetails>) => setDetails((d) => (d ? { ...d, ...patch, uncertain: d.uncertain.filter((k) => !(k in patch)) } : d));
  const unsure = (key: string) => !!details?.uncertain.includes(key);
  const field = (key: keyof CardDetails, label: string, node: ReactNode) => (
    <Form.Item label={label} validateStatus={unsure(key) ? 'warning' : undefined} help={unsure(key) ? 'Hard to read on the card: check this' : undefined} style={{ marginBottom: 12 }}>
      {node}
    </Form.Item>
  );
  const text = (key: 'firstName' | 'lastName' | 'jobTitle' | 'company' | 'website' | 'linkedin' | 'facebook' | 'instagram' | 'x' | 'tiktok', label: string) =>
    field(key, label, <Input size="large" aria-label={label} value={details?.[key] ?? ''} onChange={(e) => set({ [key]: e.target.value || null })} />);

  const onSave = () => {
    if (!details) return;
    const existing = target && target !== NEW ? target : null;
    save
      .mutateAsync({ id: s.id, details, eventTagId: eventTagId || null, existingContactId: existing, ...audience })
      .then((r) => {
        const name = [details.firstName, details.lastName].filter(Boolean).join(' ') || 'Contact';
        // toasts render outside the router, so no <Link> here
        message.success({ content: <span>{existing ? `${name} updated` : `${name} saved`}. <a onClick={() => navigate(`/contacts/${r.contactId}`)}>Open</a></span>, duration: 4 });
        goNext();
      })
      .catch((e) => message.error(errorMessage(e)));
  };
  const onDiscard = () =>
    discard
      .mutateAsync(s.id)
      .then(() => {
        message.info('Card discarded');
        goNext();
      })
      .catch((e) => message.error(errorMessage(e)));
  const changeEvent = (value: string) => {
    setEventTagId(value);
    update.mutate({ id: s.id, eventTagId: value || null });
  };
  const changeAudience = (a: CardAudience) => {
    setPicked(a);
    update.mutate({ id: s.id, ...a });
  };

  const photos = (
    <Image.PreviewGroup>
      <div style={{ display: 'flex', gap: 8 }}>
        {[s.frontFileId, s.backFileId].filter((f): f is string => !!f).map((f, i) => (
          <div key={f} style={{ flex: 1, minWidth: 0 }}>
            <Image src={fileUrl(f)} alt={i ? 'Back of the card' : 'Front of the card'} style={{ width: '100%', borderRadius: 8, border: `1px solid ${palette.border}` }} />
          </div>
        ))}
      </div>
    </Image.PreviewGroup>
  );

  return (
    <div className="page page-narrow" style={{ paddingBottom: 24 }}>
      <PageHeader
        title="Check card"
        breadcrumb={[{ title: 'Scan cards', to: SCAN_PATH }, { title: 'Check card' }]}
        subtitle={position >= 0 && ready.length > 1 ? `Card ${position + 1} of ${ready.length} to check` : undefined}
      />
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        <Card>{photos}</Card>

        {s.status === 'PROCESSING' && (
          <Card>
            <Space>
              <Spin /> Reading the card…
            </Space>
          </Card>
        )}
        {s.status === 'SAVED' && (
          <Alert type="success" showIcon title="This card is saved" action={s.contactId ? <Link to={`/contacts/${s.contactId}`}>Open contact</Link> : undefined} />
        )}
        {s.status === 'DISCARDED' && <Alert type="info" showIcon title="This card was discarded" />}
        {s.status === 'FAILED' && !details && (
          <Alert
            type="error"
            showIcon
            title="Couldn’t read this card"
            description={s.error}
            action={
              <Space orientation="vertical">
                <Button size="small" icon={<ReloadOutlined />} loading={retry.isPending} onClick={() => retry.mutate(s.id)}>
                  Read again
                </Button>
                <Button size="small" onClick={() => setDetails(emptyCardDetails())}>
                  Type it in
                </Button>
              </Space>
            }
          />
        )}
        {s.status === 'READY' && s.error && details && <Alert type="warning" showIcon title="Only the QR code could be read" description="Check the rest against the photo." />}

        {details && (s.status === 'READY' || s.status === 'FAILED') && (
          <>
            {details.uncertain.length > 0 && <Alert type="warning" showIcon icon={<WarningOutlined />} title="Some details were hard to read. They are marked below." />}
            <Card title={<CardTitle title="Person" />}>
              <Form layout="vertical" component="div">
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    {text('firstName', 'First name')}
                  </Col>
                  <Col xs={24} sm={12}>
                    {text('lastName', 'Last name')}
                  </Col>
                </Row>
                {text('jobTitle', 'Job title')}
                {text('company', 'Company')}
              </Form>
            </Card>

            <Card title={<CardTitle title="Emails and phones" />}>
              <Form layout="vertical" component="div">
                {field(
                  'emails',
                  'Emails',
                  <Space orientation="vertical" style={{ width: '100%' }}>
                    {details.emails.map((e, i) => (
                      <Space.Compact key={i} style={{ width: '100%' }}>
                        <Input size="large" inputMode="email" aria-label={`Email ${i + 1}`} value={e.email} onChange={(ev) => set({ emails: details.emails.map((x, j) => (j === i ? { ...x, email: ev.target.value } : x)) })} />
                        <Select size="large" aria-label={`Email ${i + 1} label`} style={{ width: 120 }} allowClear placeholder="Label" value={e.label ?? undefined} options={EMAIL_LABEL_OPTIONS} onChange={(v) => set({ emails: details.emails.map((x, j) => (j === i ? { ...x, label: v ?? null } : x)) })} />
                        <Button size="large" aria-label={`Remove email ${i + 1}`} icon={<MinusCircleOutlined />} onClick={() => set({ emails: details.emails.filter((_, j) => j !== i) })} />
                      </Space.Compact>
                    ))}
                    <Button icon={<PlusOutlined />} onClick={() => set({ emails: [...details.emails, { email: '', label: 'Work' }] })}>
                      Add email
                    </Button>
                  </Space>,
                )}
                {field(
                  'phones',
                  'Phones',
                  <Space orientation="vertical" style={{ width: '100%' }}>
                    {details.phones.map((p, i) => (
                      <Space.Compact key={i} style={{ width: '100%' }}>
                        <Input size="large" inputMode="tel" aria-label={`Phone ${i + 1}`} value={p.number} onChange={(ev) => set({ phones: details.phones.map((x, j) => (j === i ? { ...x, number: ev.target.value } : x)) })} />
                        <Select size="large" aria-label={`Phone ${i + 1} label`} style={{ width: 120 }} allowClear placeholder="Label" value={p.label ?? undefined} options={PHONE_LABEL_OPTIONS} onChange={(v) => set({ phones: details.phones.map((x, j) => (j === i ? { ...x, label: v ?? null } : x)) })} />
                        <Button size="large" aria-label={`Remove phone ${i + 1}`} icon={<MinusCircleOutlined />} onClick={() => set({ phones: details.phones.filter((_, j) => j !== i) })} />
                      </Space.Compact>
                    ))}
                    <Button icon={<PlusOutlined />} onClick={() => set({ phones: [...details.phones, { number: '', label: 'Mobile' }] })}>
                      Add phone
                    </Button>
                  </Space>,
                )}
              </Form>
            </Card>

            <Card title={<CardTitle title="Web and address" />}>
              <Form layout="vertical" component="div">
                {LINKS.filter((l) => allLinks || l.key === 'website' || l.key === 'linkedin' || details[l.key] || unsure(l.key)).map((l) => (
                  <div key={l.key}>{text(l.key, l.label)}</div>
                ))}
                {!allLinks && LINKS.some((l) => !(l.key === 'website' || l.key === 'linkedin' || details[l.key])) && (
                  <Button type="link" style={{ padding: 0, marginBottom: 12 }} onClick={() => setAllLinks(true)}>
                    More social links
                  </Button>
                )}
                {field(
                  'address',
                  'Address',
                  details.address ? (
                    <Space orientation="vertical" style={{ width: '100%' }}>
                      {(['line1', 'line2', 'city', 'postalCode', 'state', 'country'] as const).map((k) => (
                        <Input
                          key={k}
                          size="large"
                          placeholder={{ line1: 'Street', line2: 'Building, floor', city: 'City', postalCode: 'Postal code', state: 'State / region', country: 'Country' }[k]}
                          aria-label={k}
                          value={details.address?.[k] ?? ''}
                          onChange={(e) => set({ address: { ...details.address!, [k]: e.target.value || null } })}
                        />
                      ))}
                      <Button size="small" onClick={() => set({ address: null })}>
                        Remove address
                      </Button>
                    </Space>
                  ) : (
                    <Button icon={<PlusOutlined />} onClick={() => set({ address: { line1: null, line2: null, city: null, state: null, postalCode: null, country: null } })}>
                      Add address
                    </Button>
                  ),
                )}
                {field('note', 'Note', <Input.TextArea aria-label="Note" autoSize={{ minRows: 2, maxRows: 6 }} value={details.note ?? ''} onChange={(e) => set({ note: e.target.value || null })} placeholder="Anything else from the card, or where you talked" />)}
              </Form>
            </Card>

            <Card title={<CardTitle title="Where did you meet them?" subtitle={eventName ? `Saved in the contact's notes as "Where we met: ${eventName}", and added as a tag.` : 'Saved in the contact\'s notes and added as a tag.'} />}>
              <EventPicker value={eventTagId} onChange={changeEvent} extra={s.eventTag} size="middle" />
            </Card>

            <Card title={<CardTitle title="Tags, lists and segments" subtitle="Where the contact goes when you save this card." />}>
              <CardAudienceFields value={audience} onChange={changeAudience} />
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                <Typography.Text type="secondary" style={{ fontSize: 13, marginRight: 4 }}>
                  Always tagged:
                </Typography.Text>
                {addedBy.data && <Tag color="blue">{addedBy.data.name}</Tag>}
                {eventName && <Tag>{eventName}</Tag>}
              </div>
            </Card>

            {!!matches.data?.length && (
              <Card title={<CardTitle title="Already in the CRM?" />}>
                <Radio.Group value={target ?? NEW} onChange={(e) => setTarget(e.target.value)} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {matches.data.map((m) => (
                    <Radio key={m.id} value={m.id}>
                      Add to <Link to={`/contacts/${m.id}`} target="_blank">{[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email || 'Unnamed contact'}</Link>
                      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                        {[m.jobTitle, m.organizationName, m.email ?? m.phone].filter(Boolean).join(' · ')} ({m.reasons.map((r) => REASON[r]).join(', ')})
                      </Typography.Text>
                    </Radio>
                  ))}
                  <Radio value={NEW}>Create a new contact</Radio>
                </Radio.Group>
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
                  Adding to an existing contact fills in what it is missing and adds new emails and phones; nothing is overwritten.
                </Typography.Text>
              </Card>
            )}

            {/* fixed to the bottom so the main action is always in thumb reach on a phone */}
            <div style={{ position: 'sticky', bottom: 0, background: palette.canvas, padding: '12px 0', display: 'flex', gap: 12, zIndex: 2 }}>
              <Button size="large" danger icon={<DeleteOutlined />} loading={discard.isPending} onClick={onDiscard}>
                Discard
              </Button>
              <Button size="large" type="primary" block icon={<CheckOutlined />} loading={save.isPending} onClick={onSave}>
                {target && target !== NEW ? 'Approve and update' : 'Approve and save'}
              </Button>
            </div>
          </>
        )}
      </Space>
    </div>
  );
}
